/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import UserGroupEditForm from '../Forms/UserGroupEditForm';
import BotAvatar from '../UI/BotAvatar';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import SideModal from '../Layout/SideModal';
import DocumentTitle from '../DocumentTitle';
import { HrwizIcon } from '../Icons/HrwizIcon';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { GearIcon } from '../Icons/GearIcon';
import { LiveChatIcon } from '../Icons/LiveChatIcon';

// Misc imports
import { useDeleteUserGroupMutation, useFetchUserGroupsQuery } from '../../store/userGroups/userGroupsApi';
import { ADDITIONAL_ROLES_ORG, DEFAULT_PAGINATION_SIZE, PERMISSIONS_ORG } from '../../constants/main';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { isEmptyObject } from '../../utils/is/isEmptyObject';

const UserGroupsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { handleMouseDown, isDragging } = useDragDetection();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const { data: userGroups, isLoading: isLoadingUserGroups } = useFetchUserGroupsQuery({
    orgId,
    urlParams: {
      ...(searchFilter.search !== '' && { name: encodeURIComponent(searchFilter.search) }),
      page: currentPage - 1,
      pageSize: pageSize
    }
  });

  const { data: bots, isLoading: isLoadingBots } = useFetchBotsQuery({ orgId }, { skip: !orgId });

  const [deleteUserGroup] = useDeleteUserGroupMutation({ fixedCacheKey: 'userGroupsTable' });

  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1, pageSize);
  }, []);

  const onGroupDelete = async (groupId) => {
    const isDeleteSuccessful = await deleteUserGroup({ orgId, userGroupId: groupId });

    if (isDeleteSuccessful) {
      if (userGroups.total - 1 === (currentPage - 1) * pageSize) {
        setCurrentPage(currentPage - 1);
      }
    }
  };

  const updateUrlSearchParams = (current = 1, pageSize = DEFAULT_PAGINATION_SIZE) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);

    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', pageSize);
    urlSearchParams.delete('sortBy');
    urlSearchParams.delete('order');

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination) => {
    updateUrlSearchParams(pagination.current, pagination.pageSize);
  };

  return (
    <Fragment>
      <DocumentTitle title={t('User Groups')} />

      <Space
        size={16}
        align="center"
        className="justify-content-between mb-4 w-100"
        wrap
      >
        <Input
          size="large"
          placeholder={t('Search in contents')}
          allowClear
          prefix={<SearchOutlined className="text-muted" />}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchFilter({ ...searchFilter, search: e.target.value });
          }}
        />

        <Button
          size="large"
          type="primary"
          className="float-right"
          disabled={!bots}
          icon={<PlusOutlined />}
          onClick={() => setSelectedGroup({})}
        >
          {t('Add Group')}
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        dataSource={userGroups && userGroups.items}
        loading={isLoadingUserGroups || isLoadingBots}
        onChange={handleTableChange}
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(!isDragging()){
              setSelectedGroup(record);
            }
          }
        })}
        rowClassName="cursor-pointer"
        pagination={{
          size: 'default',
          total: userGroups?.total,
          defaultCurrent: 1,
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => `${t('Total Groups')}: ${total}`
        }}
        columns={[
          {
            key: 'name',
            dataIndex: 'name',
            title: t('Group Name'),
            ellipsis: true
          },
          {
            key: 'users',
            dataIndex: 'userCount',
            title: t('Members'),
            responsive: ['sm'],
            ellipsis: true
          },
          {
            key: 'botAccess',
            dataIndex: 'tenantRoles',
            title: t('Agents'),
            responsive: ['lg'],
            render: (values) => {
              const botIds = Object.keys(values ?? {});

              if (!botIds.length) {
                return '-';
              }

              const botsToShow = bots?.items.filter(bot => botIds.includes(bot.id)) ?? [];

              return (
                <ResponsiveTagsList
                  values={
                    botsToShow.map((bot) => ({
                      icon: <BotAvatar avatarSize={24} bot={bot} showBotName={false} />,
                      label: bot.displayName,
                      value: bot.id
                    }))
                  }
                  maxTagCount={4}
                  tagRender={(tag) => {
                    const matchedTag = botsToShow.find(bot => bot.id === tag.value);
                    return (
                      <Tooltip overlayClassName="pointer-events-none" title={matchedTag.displayName}>
                        <BotAvatar avatarSize={24} bot={matchedTag} showBotName={false} className="mr-2" />
                      </Tooltip>
                    );
                  }}
                />
              );
            }
          },
          {
            key: 'orgAccess',
            dataIndex: 'organizationRoles',
            title: t('Workspace Roles'),
            responsive: ['md'],
            render: (value) => {
              const access = t(value.find(role => Object.values(PERMISSIONS_ORG).includes(role)), { context: 'user role' });
              if (access) {
                return <Typography.Text className="fw-500 fs-sm">{access}</Typography.Text>;
              }

              return '-';
            }
          },
          {
            key: 'roles',
            title: t('Application Roles'),
            dataIndex: 'organizationRoles',
            responsive: ['xl'],
            ellipsis: true,
            render: (value) => {
              const appRoles = value.filter(role => Object.values(ADDITIONAL_ROLES_ORG).includes(role));
              if (!appRoles.length) {
                return '-';
              }

              const tags = [];
              for (const role of appRoles) {
                switch (role) {
                  case ADDITIONAL_ROLES_ORG.LIVE_AGENT:
                    tags.push(
                      <Tooltip overlayClassName="pointer-events-none" title={t('Live Agent')}>
                        <Tag className="fs-base border-radius-base px-2 py-1">
                          <LiveChatIcon className="fs-lg" />
                        </Tag>
                      </Tooltip>
                    );
                    break;
                  case ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN:
                    tags.push(
                      <Tooltip overlayClassName="pointer-events-none" title={t('LiveChat Admin')}>
                        <Tag className="fs-base border-radius-base px-2 py-1">
                          <LiveChatIcon className="fs-lg"/>
                          <GearIcon className="fs-sm v-align-baseline" />
                        </Tag>
                      </Tooltip>
                    );
                    break;
                  case ADDITIONAL_ROLES_ORG.HRWIZ_EMPLOYEE:
                    tags.push(
                      <Tooltip overlayClassName="pointer-events-none" title={t('HRwiz Employee')}>
                        <Tag className="fs-base border-radius-base px-2 py-1">
                          <HrwizIcon className="fs-lg" />
                        </Tag>
                      </Tooltip>
                    );
                    break;
                  default:
                    break;
                }
              }
              return tags;

            }
          },
          {
            key: 'actions',
            title: t('Actions'),
            width: 114,
            align: 'center',
            render: (_value, record) => (
              <Space onClick={(e) => e.stopPropagation()} wrap={false} align="center">
                <Tooltip
                  overlayClassName="pointer-events-none"
                  title={t('Edit')}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditIcon />}
                    disabled={!bots}
                    onClick={() => {
                      setSelectedGroup(record);
                    }}
                  />
                </Tooltip>

                <Popconfirm
                  title={t('Delete this group?')}
                  okText={t('Delete')}
                  onConfirm={() => onGroupDelete(record.id)}
                  placement="topRight"
                  zIndex={1100}
                >
                  <Tooltip
                    overlayClassName="pointer-events-none"
                    title={t('Delete')}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      icon={<DeleteIcon />}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            )
          }
        ]}
      />

      <SideModal
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        title={isEmptyObject(selectedGroup) ? t('Create New User Group') : t('Edit User Group')}
        bodyClassName="p-0"
        destroyOnClose
      >
        <UserGroupEditForm
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          onCancel={() => setSelectedGroup(null)}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>
    </Fragment>
  );
};

export default UserGroupsTable;
